import { useState } from 'react';
import styles from './ManipulativesSection.module.css';

type Kind = 'hundred' | 'ten' | 'one';

interface Block {
  id: string;
  kind: Kind;
}

const KINDS: { kind: Kind; addLabel: string; selectionLabel: string }[] = [
  { kind: 'hundred', addLabel: 'Add a hundred block', selectionLabel: 'Hundred' },
  { kind: 'ten', addLabel: 'Add a ten rod', selectionLabel: 'Ten' },
  { kind: 'one', addLabel: 'Add a one cube', selectionLabel: 'One' },
];

let nextId = 0;
function newBlock(kind: Kind): Block {
  nextId += 1;
  return { id: `b-${nextId}`, kind };
}

/**
 * Base-10 number modeling — three rows, one per place value:
 *
 *   [Hundred sample model] [Hundreds bin]
 *   [Ten sample model]     [Tens bin       (wide)       ]
 *   [One sample model]     [Ones bin (5 per row)]
 *
 * The left "selection" column is a vertical stack of clickable
 * block samples — tap one to drop a fresh block into its bin on
 * the right. The placed blocks themselves are the visual counter
 * for each place (no separate text count line).
 *
 * Block visuals are CSS-only (gradient grid lines) so they scale
 * cleanly at any KioskStage zoom.
 */
export function ManipulativesSection() {
  const [blocks, setBlocks] = useState<Block[]>([]);

  function add(kind: Kind) {
    setBlocks((prev) => [...prev, newBlock(kind)]);
  }

  function remove(id: string) {
    setBlocks((prev) => prev.filter((b) => b.id !== id));
  }

  function clear() {
    setBlocks([]);
  }

  const byKind: Record<Kind, Block[]> = { hundred: [], ten: [], one: [] };
  for (const b of blocks) byKind[b.kind].push(b);

  return (
    <section className={styles.section} data-testid="manipulatives-section">
      <header className={styles.header}>
        <h2 className={styles.heading}>Number modeling</h2>
        <button
          type="button"
          onClick={clear}
          className={styles.clearButton}
          data-testid="block-clear"
        >
          Clear blocks
        </button>
      </header>

      <div className={styles.tray} data-testid="block-tray">
        {KINDS.map(({ kind, addLabel, selectionLabel }) => (
          <div key={kind} className={styles.row}>
            <button
              type="button"
              onClick={() => add(kind)}
              className={styles.selection}
              aria-label={addLabel}
              data-testid={`block-add-${kind}`}
              data-kind={kind}
            >
              <span className={`${styles.sample} ${styles[kind]}`} aria-hidden="true" />
              <span className={styles.selectionLabel}>{selectionLabel}</span>
            </button>

            <div className={`${styles.bin} ${styles[`bin_${kind}`]}`}>
              {byKind[kind].map((b) => (
                <button
                  key={b.id}
                  type="button"
                  onClick={() => remove(b.id)}
                  className={`${styles.block} ${styles[kind]}`}
                  aria-label={`Remove ${kind} block`}
                  data-testid={`block-${kind}`}
                  data-block-id={b.id}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
